/**
 * OutputAnalytics - Renders 8 output charts in a 2x4 grid layout.
 *
 * Charts:
 *   Row 1: Daily Stool Count Bar | Daily Urine Count Bar
 *   Row 2: Stool Shape Scatter   | Stool Size Scatter
 *   Row 3: Stool Color Stacked Bar | Urine Color Line
 *   Row 4: Urine Type Histogram  | Urine Color Stacked Bar (optional)
 *
 * All charts are always rendered (never hidden). When no data is available,
 * charts display an empty state (flat line at 0 or empty bars).
 *
 * Requirements: 8.1, 8.2, 8.3, 8.4, 9.1, 9.2, 9.3, 9.4, 10.1, 10.2, 10.3,
 *              11.1, 11.2, 11.3, 12.1, 12.2, 12.3, 13.1, 13.2, 13.3,
 *              14.1, 14.2, 15.1, 15.2, 15.3
 */

import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Line, Scatter } from 'react-chartjs-2';
import type {
  MonthlyReportViewModel,
  ChartDataSeries,
  ScatterDataSeries,
  StackedBarDataSeries,
  HistogramDataSeries,
} from '../types';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// ─── Props ───────────────────────────────────────────────────────────────────

export interface OutputAnalyticsProps {
  charts: MonthlyReportViewModel['outputCharts'];
}

// ─── Bristol Scale Labels ────────────────────────────────────────────────────

const BRISTOL_SCALE_LABELS: Record<number, string> = {
  1: '1 - Hard lumps',
  2: '2 - Lumpy sausage',
  3: '3 - Cracked sausage',
  4: '4 - Smooth snake',
  5: '5 - Soft blobs',
  6: '6 - Mushy',
  7: '7 - Liquid',
};

const AMOUNT_SCORE_LABELS: Record<number, string> = {
  1: 'Small',
  2: 'Moderate',
  3: 'Large',
};

const URINE_COLOR_LABELS: Record<number, string> = {
  1: 'Clear',
  2: 'Light Yellow',
  3: 'Yellow',
  4: 'Dark Yellow',
  5: 'Amber',
  6: 'Brown',
  7: 'Red',
};

// ─── Chart Container ─────────────────────────────────────────────────────────

const ChartContainer: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div
    className="output-chart-container"
    style={{
      padding: '0.5rem',
      border: '1px solid #e9ecef',
      borderRadius: '4px',
      backgroundColor: '#fff',
    }}
  >
    <h4
      style={{
        margin: '0 0 0.5rem 0',
        fontSize: '0.85rem',
        color: '#495057',
        textAlign: 'center',
      }}
    >
      {title}
    </h4>
    <div style={{ position: 'relative', height: '280px' }}>{children}</div>
  </div>
);

// ─── Daily Count Bar Chart ───────────────────────────────────────────────────

const DailyCountBarChart: React.FC<{
  series: ChartDataSeries;
  color: string;
  yLabel: string;
}> = ({ series, color, yLabel }) => {
  const data = {
    labels: series.labels,
    datasets: series.datasets.map((ds) => ({
      label: ds.label,
      data: ds.data,
      backgroundColor: color,
      borderColor: color,
      borderWidth: 1,
    })),
  };

  const tooltipNotes = series.datasets[0]?.tooltipNotes;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          afterBody: (context: { dataIndex: number }[]) => {
            if (!tooltipNotes || !context[0]) return '';
            const note = tooltipNotes[context[0].dataIndex];
            return note ? `Note: ${note}` : '';
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: 'Date' },
        ticks: { maxRotation: 45, autoSkip: true },
      },
      y: {
        title: { display: true, text: yLabel },
        beginAtZero: true,
        ticks: { stepSize: 1 },
      },
    },
  };

  return <Bar data={data} options={options as any} />;
};

// ─── Scatter Chart (Stool Shape / Stool Size) ────────────────────────────────

const OutputScatterChart: React.FC<{
  series: ScatterDataSeries;
  yMin: number;
  yMax: number;
  yLabels: Record<number, string>;
  yTitle: string;
  color: string;
}> = ({ series, yMin, yMax, yLabels, yTitle, color }) => {
  const data = {
    datasets: series.datasets.map((ds) => ({
      label: ds.label,
      data: ds.data.map((pt) => ({ x: pt.x, y: pt.y })),
      backgroundColor: color,
      borderColor: color,
      pointRadius: 5,
      pointHoverRadius: 7,
    })),
  };

  // Build tooltip notes lookup
  const tooltipMap: Record<string, string> = {};
  series.datasets.forEach((ds) => {
    ds.data.forEach((pt, idx) => {
      if (pt.tooltip) {
        tooltipMap[`${pt.x}_${pt.y}_${idx}`] = pt.tooltip;
      }
    });
  });

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const raw = context.raw as { x: string; y: number };
            const label = yLabels[raw.y] || String(raw.y);
            return `${raw.x}: ${label}`;
          },
          afterBody: (context: any[]) => {
            if (!context[0]) return '';
            const dsIndex = context[0].datasetIndex;
            const ptIndex = context[0].dataIndex;
            const ds = series.datasets[dsIndex];
            if (ds && ds.data[ptIndex]?.tooltip) {
              return `Note: ${ds.data[ptIndex].tooltip}`;
            }
            return '';
          },
        },
      },
    },
    scales: {
      x: {
        type: 'category' as const,
        title: { display: true, text: 'Date' },
        ticks: { maxRotation: 45, autoSkip: true },
      },
      y: {
        title: { display: true, text: yTitle },
        min: yMin,
        max: yMax,
        ticks: {
          stepSize: 1,
          callback: (value: number | string) => {
            const numVal = typeof value === 'string' ? parseInt(value, 10) : value;
            return yLabels[numVal] || String(numVal);
          },
        },
      },
    },
  };

  return <Scatter data={data} options={options as any} />;
};

// ─── Stacked Bar Chart ───────────────────────────────────────────────────────

const StackedBarChart: React.FC<{ series: StackedBarDataSeries }> = ({ series }) => {
  const data = {
    labels: series.labels,
    datasets: series.datasets.map((ds) => ({
      label: ds.label,
      data: ds.data,
      backgroundColor: ds.backgroundColor,
      borderWidth: 1,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: { boxWidth: 12, font: { size: 10 } },
      },
    },
    scales: {
      x: {
        stacked: true,
        title: { display: true, text: 'Date' },
        ticks: { maxRotation: 45, autoSkip: true },
      },
      y: {
        stacked: true,
        title: { display: true, text: 'Count' },
        beginAtZero: true,
        ticks: { stepSize: 1 },
      },
    },
  };

  return <Bar data={data} options={options as any} />;
};

// ─── Urine Color Line Chart ──────────────────────────────────────────────────

const UrineColorLineChart: React.FC<{ series: ChartDataSeries }> = ({ series }) => {
  const tooltipNotes = series.datasets[0]?.tooltipNotes;

  const data = {
    labels: series.labels,
    datasets: series.datasets.map((ds) => ({
      label: ds.label,
      data: ds.data,
      borderColor: 'rgba(255, 193, 7, 1)',
      backgroundColor: 'rgba(255, 193, 7, 0.2)',
      fill: false,
      tension: 0.2,
      pointRadius: 4,
      pointHoverRadius: 6,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const val = context.parsed.y;
            const label = URINE_COLOR_LABELS[val] || String(val);
            return `Color: ${label}`;
          },
          afterBody: (context: { dataIndex: number }[]) => {
            if (!tooltipNotes || !context[0]) return '';
            const note = tooltipNotes[context[0].dataIndex];
            return note ? `Note: ${note}` : '';
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: 'Date' },
        ticks: { maxRotation: 45, autoSkip: true },
      },
      y: {
        title: { display: true, text: 'Urine Color' },
        min: 1,
        max: 7,
        ticks: {
          stepSize: 1,
          callback: (value: number | string) => {
            const numVal = typeof value === 'string' ? parseInt(value, 10) : value;
            return URINE_COLOR_LABELS[numVal] || String(numVal);
          },
        },
      },
    },
  };

  return <Line data={data} options={options as any} />;
};

// ─── Urine Type Histogram ────────────────────────────────────────────────────

const UrineTypeHistogramChart: React.FC<{ series: HistogramDataSeries }> = ({
  series,
}) => {
  const data = {
    labels: series.labels,
    datasets: [
      {
        label: 'Count',
        data: series.data,
        backgroundColor: 'rgba(23, 162, 184, 0.7)',
        borderColor: 'rgba(23, 162, 184, 1)',
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            return `${context.label}: ${context.parsed.y}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: 'Urine Type' },
      },
      y: {
        title: { display: true, text: 'Count' },
        beginAtZero: true,
        ticks: { stepSize: 1 },
      },
    },
  };

  return <Bar data={data} options={options as any} />;
};

// ─── Main Component ──────────────────────────────────────────────────────────

const OutputAnalytics: React.FC<OutputAnalyticsProps> = ({ charts }) => {
  return (
    <div className="output-analytics" style={{ padding: '1rem' }}>
      <div
        className="output-analytics-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '1rem',
        }}
      >
        {/* Row 1: Stool Shape | Stool Size */}
        <ChartContainer title="Stool Shape (Bristol Scale)">
          <OutputScatterChart
            series={charts.stoolShape}
            yMin={1}
            yMax={7}
            yLabels={BRISTOL_SCALE_LABELS}
            yTitle="Bristol Scale"
            color="rgba(139, 90, 43, 0.8)"
          />
        </ChartContainer>

        <ChartContainer title="Stool Size">
          <OutputScatterChart
            series={charts.stoolSize}
            yMin={1}
            yMax={3}
            yLabels={AMOUNT_SCORE_LABELS}
            yTitle="Amount Score"
            color="rgba(108, 117, 125, 0.8)"
          />
        </ChartContainer>

        {/* Row 2: Stool Color & Count | Urine Color & Count */}
        <ChartContainer title="Stool Color & Toilet Count Distribution">
          <StackedBarChart series={charts.stoolColorStacked} />
        </ChartContainer>

        <ChartContainer title="Urine Color & Toilet Count Distribution">
          {charts.urineColorStacked ? (
            <StackedBarChart series={charts.urineColorStacked} />
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#6c757d',
                fontStyle: 'italic',
                fontSize: '0.85rem',
              }}
            >
              No urine color detail data available
            </div>
          )}
        </ChartContainer>
      </div>
    </div>
  );
};

export default OutputAnalytics;
